// Admin portal shell — redesigned per the Claude Design handoff
// (`ProProp Admin.html`). 240px sidebar with section-divided nav,
// counter chips for hot inboxes, ⌘K search hint, environment pill
// in the top utility bar, and an operator signature footer.

import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import type { CSSProperties, ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { Icon } from "@/components/admin-ui";
import { useAdminArticles, useQueueStats } from "@/lib/articles-hooks";

type NavId =
  | "compose" | "review" | "articles" | "authors" | "taxonomy"
  | "jobs" | "runs" | "tables" | "addproject" | "reconcile"
  | "modReviews" | "modComments"
  | "supportInbox"
  | "users" | "stats"
  | "audit" | "settings";

const NAV_PATH: Record<NavId, string> = {
  compose:   "/editorial/compose",
  review:    "/editorial/review",
  articles:  "/editorial/articles",
  authors:   "/editorial/authors",
  taxonomy:  "/editorial/topics",
  jobs:      "/dataengine/jobs",
  runs:      "/dataengine/runs",
  tables:    "/dataengine/tables",
  addproject: "/dataengine/addproject",
  reconcile: "/dataengine/reconcile",
  modReviews: "/moderation/property-reviews",
  modComments: "/moderation/article-comments",
  supportInbox: "/support/inbox",
  users:     "/accounts/users",
  stats:     "/accounts/stats",
  audit:     "/system/audit",
  settings:  "/system/settings",
};

export function AdminShell() {
  const { admin, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  // Sidebar counts come straight from the article store. useQueueStats
  // hits /admin/articles/queue/stats; useAdminArticles {} pulls the
  // total. Both fall back to the in-memory mock when VITE_ARTICLES_API
  // is unset, so the badges stay populated in offline dev.
  const { stats } = useQueueStats();
  const { total: articlesCount } = useAdminArticles({});
  const reviewQueueCount = stats.awaiting_review + stats.in_review + stats.changes_requested;

  return (
    <div style={shellOuter}>
      {/* ── Sidebar ─────────────────────────────────────────── */}
      <aside style={shellSidebar}>
        {/* Brand */}
        <div style={{ padding: "18px 18px 14px", borderBottom: "1px solid var(--rule)" }}>
          <div
            style={{
              display: "flex", alignItems: "center", gap: 8,
              fontFamily: "var(--font-ui)", fontSize: 15.5, fontWeight: 600,
              letterSpacing: "-0.012em", color: "var(--ink)",
            }}
          >
            <span
              style={{
                width: 22, height: 22, borderRadius: 6, background: "var(--accent)",
                color: "#f4f1ea", display: "inline-flex", alignItems: "center",
                justifyContent: "center", fontFamily: "var(--font-serif)",
                fontSize: 13, fontWeight: 600, letterSpacing: "-0.04em",
              }}
            >
              P
            </span>
            ProProp<span style={{ color: "var(--accent)" }}>.</span>
          </div>
          <div
            style={{
              marginTop: 5, fontFamily: "var(--font-ui)", fontSize: 10,
              color: "var(--ink-4)", letterSpacing: "0.13em", textTransform: "uppercase",
              fontWeight: 600, whiteSpace: "nowrap",
            }}
          >
            Operations console · {envBadge(admin?.env).label}
          </div>
        </div>

        {/* Search hint (non-functional placeholder; ⌘K hook can land later) */}
        <div style={{ padding: "12px 12px 6px" }}>
          <button style={shellSearchBtn} type="button">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--ink-3)" }}>
              {Icon.search}
              <span>Search anything…</span>
            </span>
            <span style={shellKbdHint}>⌘K</span>
          </button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: "auto", padding: "6px 10px 14px" }}>
          <ShellSection>Editorial</ShellSection>
          <ShellNav id="compose"  label="Compose" />
          <ShellNav id="review"   label="Review queue"     count={reviewQueueCount} hot />
          <ShellNav id="articles" label="All articles"     count={articlesCount} />
          <ShellNav id="authors"  label="Authors & desks" />
          <ShellNav id="taxonomy" label="Topics & tags" />

          <ShellSection>Data engine</ShellSection>
          <ShellNav id="jobs"      label="Jobs" />
          <ShellNav id="runs"      label="Run history" />
          <ShellNav id="tables"    label="Tables" />
          <ShellNav id="addproject" label="Add project" />
          <ShellNav id="reconcile" label="Data Studio" />

          <ShellSection>Moderation</ShellSection>
          <ShellNav id="modReviews" label="Property reviews" />
          <ShellNav id="modComments" label="Article comments" />

          <ShellSection>Support</ShellSection>
          <ShellNav id="supportInbox" label="Inbox" />

          <ShellSection>Accounts</ShellSection>
          <ShellNav id="users" label="Users" />
          <ShellNav id="stats" label="Stats" />

          <ShellSection>System</ShellSection>
          <ShellNav id="audit"    label="Audit log" />
          <ShellNav id="settings" label="Settings" />
        </nav>

        {/* Operator signature */}
        <div
          style={{
            borderTop: "1px solid var(--rule)", padding: "12px 14px",
            display: "flex", alignItems: "center", gap: 10,
          }}
        >
          <span
            style={{
              width: 32, height: 32, borderRadius: 999, flexShrink: 0,
              background: "linear-gradient(140deg, #1d4a3a, #2d6e54)",
              color: "#f4f1ea", display: "inline-flex", alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 600, letterSpacing: 0.4,
            }}
          >
            {(admin?.name ? initials(admin.name) : (admin?.email ?? "??").slice(0, 2)).toUpperCase()}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: "var(--ink)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {admin?.name ?? "—"}
            </div>
            <div style={{ fontSize: 10.5, color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {admin?.email ?? ""}
            </div>
          </div>
          <button
            title="Sign out"
            onClick={() => void handleSignOut()}
            style={{
              background: "transparent", border: "1px solid var(--rule)", borderRadius: 6,
              padding: "5px 8px", color: "var(--ink-3)", cursor: "pointer", fontSize: 11,
            }}
          >
            ↪
          </button>
        </div>
      </aside>

      {/* ── Main column ─────────────────────────────────────── */}
      <main style={shellMain}>
        {/* Top utility bar — breadcrumb lives in the page; this bar holds
            the environment pill + notifications and a back-pointer link */}
        <div style={shellTopBar}>
          <Breadcrumb path={location.pathname} />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* PROP-202: no fabricated unread count — there is no
                notifications data source yet, so the bell carries no badge
                rather than a hardcoded "5" on every page. */}
            <button style={shellChipBtn} title="Notifications">
              {Icon.bell}
            </button>
            {(() => {
              const badge = envBadge(admin?.env);
              return (
                <span style={{ ...shellEnvPill, cursor: "default" }} title={`Environment: ${badge.label}`}>
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: badge.dot }} />
                  {badge.label}
                </span>
              );
            })()}
          </div>
        </div>

        {/* Page body */}
        <div style={{ flex: 1, minHeight: 0, overflow: "auto", background: "var(--bg)" }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/* ─── Sidebar primitives ────────────────────────────────────────── */
function ShellSection({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "14px 10px 6px" }}>
      <div
        style={{
          fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600,
          letterSpacing: "0.13em", textTransform: "uppercase", color: "var(--ink-4)",
          whiteSpace: "nowrap", flexShrink: 0,
        }}
      >
        {children}
      </div>
      <div style={{ flex: 1, height: 1, background: "var(--rule)" }} />
    </div>
  );
}

function ShellNav({ id, label, count, hot }: { id: NavId; label: string; count?: number | string; hot?: boolean }) {
  const to = NAV_PATH[id];
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        width: "100%", display: "flex", alignItems: "center", gap: 9,
        padding: "7px 10px 7px 11px", borderRadius: 7,
        background: isActive ? "var(--accent-soft)" : "transparent",
        color: isActive ? "var(--accent-ink, #0e2c22)" : "var(--ink-2)",
        cursor: "pointer", textAlign: "left", textDecoration: "none",
        fontFamily: "var(--font-ui)", fontSize: 12.5,
        fontWeight: isActive ? 500 : 400, letterSpacing: "-0.005em",
        position: "relative",
      } as CSSProperties)}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              style={{
                position: "absolute", left: -10, top: 8, bottom: 8, width: 2.5,
                background: "var(--accent)", borderRadius: 2,
              }}
            />
          )}
          <span style={{ flex: 1 }}>{label}</span>
          {count !== undefined && (
            <span
              style={{
                padding: "1px 7px", borderRadius: 999, minWidth: 18, textAlign: "center",
                background: hot && !isActive
                  ? "rgba(122,45,31,0.10)"
                  : isActive
                  ? "var(--bg)"
                  : "var(--surface-2)",
                color: hot && !isActive
                  ? "#7a2d1f"
                  : isActive
                  ? "var(--accent)"
                  : "var(--ink-3)",
                fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 600,
              }}
            >
              {count}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/* ─── Breadcrumb derived from current path ─────────────────────── */
const CRUMB_LABELS: Record<string, string> = {
  editorial:  "Editorial",
  compose:    "Compose",
  review:     "Review queue",
  articles:   "All articles",
  authors:    "Authors & desks",
  topics:     "Topics & tags",
  dataengine: "Data engine",
  jobs:       "Jobs",
  runs:       "Run history",
  tables:     "Tables",
  addproject: "Add project",
  reconcile:  "Data Studio",
  moderation: "Moderation",
  "property-reviews": "Property reviews",
  "article-comments": "Article comments",
  support:    "Support",
  inbox:      "Inbox",
  accounts:   "Accounts",
  users:      "Users",
  stats:      "Stats",
  system:     "System",
  audit:      "Audit log",
  settings:   "Settings",
};

function Breadcrumb({ path }: { path: string }) {
  const segments = path.split("/").filter(Boolean);
  // For routes like /editorial/articles/:id we collapse the trailing
  // segment label to "Article" — the page header itself shows the title.
  const labels = segments.map((s, i) => {
    if (i === segments.length - 1 && /^[a-z]-\d/.test(s)) return "Article";
    // /editorial/compose/:id — the trailing segment is the draft's
    // article id; show "Draft" rather than a raw uuid.
    if (i === segments.length - 1 && segments[i - 1] === "compose") return "Draft";
    // /dataengine/reconcile/:slug — surface the slug verbatim as the
    // trailing crumb; the page header itself is the human-readable title.
    if (i === segments.length - 1 && segments[i - 1] === "reconcile") {
      return decodeURIComponent(s);
    }
    // /support/inbox/:id — the trailing segment is the thread id; the page
    // header itself shows the subject, so the crumb reads "Thread".
    if (i === segments.length - 1 && segments[i - 1] === "inbox") return "Thread";
    return CRUMB_LABELS[s] ?? s;
  });
  if (labels.length === 0) labels.push("Admin");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, fontFamily: "var(--font-ui)" }}>
      {labels.map((label, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: 12,
              color: i === labels.length - 1 ? "var(--ink)" : "var(--ink-3)",
              fontWeight: i === labels.length - 1 ? 500 : 400,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
          {i < labels.length - 1 && <span style={{ fontSize: 11, color: "var(--ink-4)" }}>›</span>}
        </span>
      ))}
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return parts[0][0] + parts[parts.length - 1][0];
}

/* ─── Styles ───────────────────────────────────────────────────── */
const shellOuter: CSSProperties = {
  display: "flex", height: "100dvh", overflow: "hidden", background: "var(--bg)",
  fontFamily: "var(--font-ui)", color: "var(--ink)",
};
const shellSidebar: CSSProperties = {
  width: 240, flexShrink: 0, borderRight: "1px solid var(--rule)",
  background: "var(--surface)", display: "flex", flexDirection: "column",
};
const shellMain: CSSProperties = {
  flex: 1, minWidth: 0, display: "flex", flexDirection: "column", background: "var(--bg)",
};
const shellTopBar: CSSProperties = {
  flex: "none", height: 46, padding: "0 18px 0 22px",
  borderBottom: "1px solid var(--rule)", background: "var(--surface)",
  display: "flex", alignItems: "center", justifyContent: "space-between",
};
const shellSearchBtn: CSSProperties = {
  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "7px 11px", borderRadius: 7, gap: 6,
  background: "var(--bg)", border: "1px solid var(--rule)",
  fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-3)",
  cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden",
};
const shellKbdHint: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--ink-4)",
  background: "var(--surface-2)", padding: "1px 6px", borderRadius: 4,
  border: "1px solid var(--rule)",
};
const shellChipBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 5,
  padding: "5px 8px", borderRadius: 6,
  background: "var(--bg)", border: "1px solid var(--rule)",
  color: "var(--ink-2)", cursor: "pointer", position: "relative",
};
// envBadge maps the backend-reported PROPWISE_ENV id to the pill's
// label + status-dot colour. Backend is the source of truth (the env
// the adminserver actually runs in), so the badge can never drift from
// a stale build-time guess. Unknown / missing env degrades to a neutral
// grey "Unknown" rather than misreporting "Production".
function envBadge(env?: string): { label: string; dot: string } {
  switch ((env ?? "").trim().toLowerCase()) {
    case "prod":
    case "production":
      return { label: "Production", dot: "#1d4a3a" }; // green
    case "staging":
      return { label: "Staging", dot: "#b45309" }; // amber
    case "":
      return { label: "Unknown", dot: "var(--ink-3)" };
    default: {
      const e = env!.trim();
      return { label: e.charAt(0).toUpperCase() + e.slice(1).toLowerCase(), dot: "var(--ink-3)" };
    }
  }
}

const shellEnvPill: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 7,
  padding: "5px 11px 5px 9px", borderRadius: 6,
  background: "var(--bg)", border: "1px solid var(--rule)",
  color: "var(--ink-2)", cursor: "pointer",
  fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 500,
};
